// Sumar imágenes al lienzo (spec §3.2, ADR-2026-09-17-agregar-imagen-es-la-biblioteca-del-diseno):
// dos pestañas. "De este proyecto" es la BIBLIOTECA del diseño —lo que subiste y lo que
// se generó acá, esté o no en el lienzo— con la tarjeta de Subir al frente (también recibe
// lo que se soltó sobre el lienzo); "Mis creaciones", las de la cuenta. Sin tipos: todas
// las imágenes tienen las mismas acciones
// (ADR-2026-09-17-todas-las-imagenes-tienen-las-mismas-acciones). Las subidas van al
// bucket de referencias
// (ADR-2026-09-17-las-imagenes-propias-del-diseno-se-suben). El pie con Agregar es fijo.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Loader2, Upload } from 'lucide-react';
import CachedImg from '../../../../../components/CachedImg';
import { HojaLateral } from '../../../../../chat-atlas/ui/HojaLateral';
import { GeneradasPickGrid, type GeneradaPick } from '../../shared/GeneradasPickGrid';
import { useDiseno } from '../disenoContext';
import { conImagenPropia, tituloDeArchivo } from '../disenoGuardado';
import { idDeImagenPropia } from '../disenosApi';
import { subirImagenDeDiseno } from '../generarEnDiseno';

type Pestana = 'proyecto' | 'creaciones';
const PESTANAS: { id: Pestana; rotulo: string }[] = [
  { id: 'proyecto', rotulo: 'De este proyecto' },
  { id: 'creaciones', rotulo: 'Mis creaciones' },
];
const SEGMENTO = 'flex-1 rounded-full px-4 py-2 text-[13.5px] font-semibold transition-colors';
const TILE = 'relative aspect-square overflow-hidden rounded-xl bg-[#F3F4F8] text-left';

export const HojaAgregarImagen: React.FC<{ archivosIniciales: File[]; onCerrar: () => void }> = ({ archivosIniciales, onCerrar }) => {
  const d = useDiseno();
  const [pestana, setPestana] = useState<Pestana>('proyecto');
  const [archivos, setArchivos] = useState<File[]>(archivosIniciales);
  const [elegidas, setElegidas] = useState<GeneradaPick[]>([]);
  const [devolver, setDevolver] = useState<string[]>([]);
  const [subiendo, setSubiendo] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const total = archivos.length + elegidas.length + devolver.length;
  const biblioteca = d.biblioteca();

  // Una URL por archivo mientras la hoja vive; se revocan al desmontar, no en el render.
  const previews = useMemo(() => archivos.map((f) => URL.createObjectURL(f)), [archivos]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  const agregar = async () => {
    if (total === 0 || subiendo || !d.companyId) return;
    setSubiendo(true);
    try {
      // Lo que no pasa por la red va primero, así queda sumado pase lo que pase con las
      // subidas: lo que vuelve de la biblioteca y las creaciones elegidas.
      for (const id of devolver) d.devolverAlLienzo(id);
      setDevolver([]);
      for (const p of elegidas) {
        d.agregarDeCreaciones({ id: p.id, ratio: 0.8, titulo: p.producto ?? 'Creación' });
      }
      setElegidas([]);
      // Se sube de a un archivo, y cada uno se saca de `archivos` apenas queda sumado al
      // diseño: si la subida 3 de 5 falla, las dos primeras ya no están en la lista, así
      // que un segundo click en "Agregar" sólo reintenta las que faltan y no las duplica.
      for (const f of archivos) {
        const subida = await subirImagenDeDiseno(f, d.companyId);
        d.setDiseno((x) => conImagenPropia(x, { id: idDeImagenPropia(), titulo: tituloDeArchivo(f.name), ...subida, creadaEn: Date.now() }));
        setArchivos((prev) => prev.filter((x) => x !== f));
      }
      onCerrar();
    } catch (e) {
      d.avisarError(e instanceof Error ? e.message : 'No se pudo agregar la imagen.');
    } finally {
      setSubiendo(false);
    }
  };

  const pie = (
    <div className="flex items-center justify-end gap-2">
      <button type="button" onClick={onCerrar} className="px-3 py-2 text-[15px] text-[#3C3C43]/60 transition-colors hover:text-[#02062B]/[0.8]">
        Cancelar
      </button>
      <button
        type="button"
        onClick={() => void agregar()}
        disabled={total === 0 || subiendo}
        className="inline-flex items-center gap-2 rounded-md bg-brand-primary px-4 py-2 text-[15px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-primary-hover disabled:opacity-60"
      >
        {subiendo && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        Agregar{total > 0 ? ` · ${total}` : ''}
      </button>
    </div>
  );

  return (
    <HojaLateral abierta titulo="Agregar imagen" bajada="Prendas, inspiraciones o texturas: en el lienzo todas tienen las mismas opciones." onCerrar={onCerrar} pie={pie}>
      <div className="flex flex-col gap-4 px-2 py-1">
        <div className="flex gap-1 rounded-full bg-black/[0.05] p-1">
          {PESTANAS.map((p) => (
            <button key={p.id} type="button" onClick={() => setPestana(p.id)} aria-pressed={pestana === p.id} className={`${SEGMENTO} ${pestana === p.id ? 'bg-white text-brand-black shadow-sm' : 'text-[#3C3C43]/60 hover:text-brand-black'}`}>
              {p.rotulo}
            </button>
          ))}
        </div>
        {pestana === 'proyecto' ? (
          <div className="grid grid-cols-3 gap-2">
            <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={(e) => setArchivos((prev) => [...prev, ...Array.from(e.target.files ?? [])])} />
            <button type="button" onClick={() => inputRef.current?.click()} className="flex aspect-square flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-black/[0.15] px-2 text-center text-[12.5px] text-[#3C3C43]/60 transition-colors hover:border-brand-primary hover:text-brand-primary">
              <Upload className="h-5 w-5" aria-hidden="true" />
              Subir
            </button>
            {archivos.map((f, i) => (
              <button key={`${f.name}-${i}`} type="button" onClick={() => setArchivos((prev) => prev.filter((_, j) => j !== i))} title="Sacar" className={`${TILE} ring-2 ring-brand-primary ring-offset-1`}>
                <img src={previews[i]} alt={f.name} className="h-full w-full object-cover" />
                <span className="absolute right-1.5 top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary text-white">
                  <Check className="h-3 w-3" aria-hidden="true" />
                </span>
              </button>
            ))}
            {biblioteca.map((img) => {
              const url = d.urlDe(img.id);
              const elegida = devolver.includes(img.id);
              return (
                <button
                  key={img.id}
                  type="button"
                  disabled={img.enLienzo}
                  onClick={() => setDevolver((prev) => (elegida ? prev.filter((x) => x !== img.id) : [...prev, img.id]))}
                  title={img.enLienzo ? 'Ya está en el lienzo' : elegida ? 'Sacar de la selección' : 'Volver a sumar al lienzo'}
                  className={`${TILE} ${img.enLienzo ? 'opacity-45' : ''} ${elegida ? 'ring-2 ring-brand-primary ring-offset-1' : ''}`}
                >
                  {url && <CachedImg src={url} alt={img.titulo} className="h-full w-full object-cover" />}
                  <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/55 to-transparent px-1.5 pb-1 pt-4 text-[11px] font-medium text-white">
                    {img.titulo || 'Imagen'}
                  </span>
                  {(img.enLienzo || elegida) && (
                    <span className={`absolute right-1.5 top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full text-white ${elegida ? 'bg-brand-primary' : 'bg-black/45'}`}>
                      <Check className="h-3 w-3" aria-hidden="true" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ) : (
          <GeneradasPickGrid
            companyId={d.companyId}
            selectedId={null}
            selectedIds={elegidas.map((p) => p.id)}
            onSelect={(pick) => setElegidas((prev) => (prev.some((p) => p.id === pick.id) ? prev.filter((p) => p.id !== pick.id) : [...prev, pick]))}
            emptyHint="Todavía no tenés creaciones para elegir."
          />
        )}
      </div>
    </HojaLateral>
  );
};
